import { useMemo, useState } from 'react';
import { REPORT_PERMISSIONS } from '@/constants/permissions';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { downloadDcrSummary } from '@/features/dcrSummary/api/dcrSummary.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { ZM_SEARCH_PLACEHOLDER } from '@/constants/app.constants';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { parseActiveStatusFilter } from '@/utils/filters';
import { getTableEmptyText } from '@/utils/listSearch';
import { dcrDashboardInfiniteQueryOptions } from '../api/dashboards.api';
import { ExportButton } from '@/components/common/ExportButton';
import { DcrDashboardFilterPanel } from '../components/DcrDashboardFilterPanel';
import { getDcrDashboardColumns } from '../components/DcrDashboardColumns';
import { DCR_TYPE_COLUMNS } from '../constants/dashboard.constants';
import { useDcrDashboardFilters } from '../hooks/useDcrDashboardFilters';
import { toDcrDrillSearch } from '../utils/dcrDashboardFilters';

export function DcrDashboardPage() {
  const controller = useDcrDashboardFilters();
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(dcrDashboardInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedUserId = useRevealLastViewedRow('dcrDashboardZms');

  const columns = useMemo(
    () => getDcrDashboardColumns(toDcrDrillSearch(controller.params), controller.params.search),
    [controller.params],
  );
  const totals = firstPageMeta?.totals;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DCR Dashboard"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: ZM_SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              requires={REPORT_PERMISSIONS.DCR_SUMMARY_EXPORT}
              onExport={() =>
                downloadDcrSummary({
                  dateFrom: controller.params.dateFrom,
                  dateTo: controller.params.dateTo,
                  reportingMemberId: controller.params.reportingMemberId,
                  companyId: controller.params.companyId,
                  departmentId: controller.params.departmentId,
                  divisionId: controller.params.divisionId,
                  districtId: controller.params.districtId,
                  thanaId: controller.params.thanaId,
                  search: controller.params.search,
                  isActive: parseActiveStatusFilter(controller.params.zmStatus),
                })
              }
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <DcrDashboardFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the dashboard"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            isFetching={isRefetching}
            stats={[
              {
                key: 'totalDcr',
                label: 'Total DCR',
                value: totals?.totalDcr,
              },
              ...DCR_TYPE_COLUMNS.map((column) => ({
                ...column,
                value: totals?.[column.key],
                hint: column.title,
              })),
            ]}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            {...getTableEmptyText(
              controller.params.search,
              'No ZMs for these filters',
              'Try a wider date range or clear a filter.',
            )}
            isRowHighlighted={(zm) => zm.userId === lastViewedUserId}
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
